import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import { CashoutInfo } from 'cashout/services/cashout-public-service';
import {
  Beneficiary,
  BeneficiaryRequest,
  CashoutStatusResponse,
  withdrawalMethodConfigV2,
} from 'cashout/services/cashout-service';
import { CashoutState } from './types';

import { RootState } from '../store';

const initialState: CashoutState = {};

export const globalSlice = createSlice({
  name: 'cashout',
  initialState,
  reducers: {
    resetStore: (state) => {
      state = initialState;
      return state;
    },
    setMethodConfig: (state, action: PayloadAction<withdrawalMethodConfigV2 | undefined>) => {
      state.methodConfig = action.payload;
    },
    setBeneficiary: (state, action: PayloadAction<Beneficiary | undefined>) => {
      state.beneficiary = action.payload;
    },
    setBeneficiaryRequest: (state, action: PayloadAction<BeneficiaryRequest | undefined>) => {
      state.beneficiaryRequest = action.payload;
    },
    setCashoutInfo: (state, action: PayloadAction<CashoutInfo | undefined>) => {
      state.cashoutInfo = action.payload;
    },
    setCashoutStatus: (state, action: PayloadAction<CashoutStatusResponse>) => {
      state.cashoutStatus = action.payload;
    },
  },
});

// Selectors
const getCashoutState = (state: RootState): CashoutState => state.cashout;
export const getBeneficiaryRequest = createSelector(
  getCashoutState,
  (cashout) => cashout.beneficiaryRequest,
);
export const getCashoutInfo = createSelector(getCashoutState, (cashout) => cashout.cashoutInfo);
export const getMethodConfig = createSelector(getCashoutState, (cashout) => cashout.methodConfig);
export const getCashoutStatus = createSelector(getCashoutState, (cashout) => cashout.cashoutStatus);
export const getBeneficiary = createSelector(getCashoutState, (cashout) => cashout.beneficiary);
export const getCashout = createSelector(getCashoutInfo, (cashoutInfo) => {
  return cashoutInfo
    ? cashoutInfo.cashout
    : {
        status: 'PENDING',
        currency: '',
        amount: 0,
        expiration: '',
        withdrawalMethodCode: '',
        withdrawalMethodName: '',
        withdrawalMethodImage: '',
      };
});
export const getMerchant = createSelector(getCashoutInfo, (cashoutInfo) => {
  if (cashoutInfo) {
    if (cashoutInfo.visualInfo) {
      return {
        name: cashoutInfo.visualInfo.merchantName ?? cashoutInfo.merchant.name,
        img: cashoutInfo.visualInfo.merchantLogo ?? cashoutInfo.merchant.img,
        url: cashoutInfo.merchant.url,
      };
    }
    return cashoutInfo.merchant;
  }
  return { name: '', img: '', url: '' };
});
export const getCashoutUser = createSelector(
  getCashoutInfo,
  (cashoutInfo) =>
    (cashoutInfo && cashoutInfo?.user) ?? {
      fullName: '',
      phone: '',
      notify: false,
      validateSameUser: false,
    },
);

const { actions, reducer } = globalSlice;

export const {
  resetStore: cashoutReset,
  setMethodConfig,
  setBeneficiary,
  setBeneficiaryRequest,
  setCashoutInfo,
  setCashoutStatus,
} = actions;

export default reducer;
